import {
  Box,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { latest } from '../types';
import type { ClientAcquisitionPoint, SafeTOrgKey } from '../types';
import { SafeTCardShell } from './SafeTCardShell';
import { safeTColors } from '../safeTColors';
import {
  buildMultiAnnualGoalStatusTooltip,
  getAnnualPaceState,
  getMultiGoalStatus,
  parseQuarterToken,
  resolveActiveGoal,
  targetNumber,
  type GoalState,
} from '../../statusUtils';
import type { DashboardActiveGoal } from '../../../../types/dashboard';
import { formatYoYSublabel } from '../../metricSeriesUtils';
import { MetricRow } from '../../atlas/cards/MetricRow';

type Props =
  | { mode: 'combined'; series: ClientAcquisitionPoint[]; goals?: DashboardActiveGoal[] }
  | { mode: 'org'; orgKey: SafeTOrgKey; series: ClientAcquisitionPoint[]; goals?: DashboardActiveGoal[] };

function goalsForYear(goals: DashboardActiveGoal[] | undefined, year: number) {
  const goal = resolveActiveGoal(goals, { grain: 'annual', year });
  return {
    engaged: targetNumber(goal, 'engaged'),
    acquired: targetNumber(goal, 'acquired'),
  };
}

export function ClientAcquisitionCard(props: Props) {
  const theme = useMantineTheme();
  const series = props.series;
  const isEmpty = !series?.length;
  const cur = isEmpty ? null : latest(series);
  const curQuarterMeta = parseQuarterToken(cur?.quarter ?? '');
  const curYear = curQuarterMeta?.year ?? null;
  const curQuarterNum = curQuarterMeta?.quarter ?? null;

  const ytdEngaged =
    curYear === null || curQuarterNum === null
      ? null
      : series
          .filter((p) => {
            const m = parseQuarterToken(p.quarter);
            return m?.year === curYear && m.quarter <= curQuarterNum;
          })
          .reduce((acc, p) => acc + p.engaged, 0);

  const ytdAcquired =
    curYear === null || curQuarterNum === null
      ? null
      : series
          .filter((p) => {
            const m = parseQuarterToken(p.quarter);
            return m?.year === curYear && m.quarter <= curQuarterNum;
          })
          .reduce((acc, p) => acc + p.acquired, 0);

  const prevYearYtdEngaged =
    curYear === null || curQuarterNum === null
      ? null
      : series
          .filter((p) => {
            const m = parseQuarterToken(p.quarter);
            return m?.year === curYear - 1 && m.quarter <= curQuarterNum;
          })
          .reduce((acc, p) => acc + p.engaged, 0);

  const prevYearYtdAcquired =
    curYear === null || curQuarterNum === null
      ? null
      : series
          .filter((p) => {
            const m = parseQuarterToken(p.quarter);
            return m?.year === curYear - 1 && m.quarter <= curQuarterNum;
          })
          .reduce((acc, p) => acc + p.acquired, 0);

  const engagedYoyPct =
    ytdEngaged !== null && prevYearYtdEngaged !== null && prevYearYtdEngaged !== 0
      ? ((ytdEngaged - prevYearYtdEngaged) / prevYearYtdEngaged) * 100
      : null;
  const acquiredYoyPct =
    ytdAcquired !== null && prevYearYtdAcquired !== null && prevYearYtdAcquired !== 0
      ? ((ytdAcquired - prevYearYtdAcquired) / prevYearYtdAcquired) * 100
      : null;
  const curYtdConversion =
    ytdEngaged !== null && ytdAcquired !== null && ytdEngaged > 0
      ? (ytdAcquired / ytdEngaged) * 100
      : null;
  const prevYtdConversion =
    prevYearYtdEngaged !== null && prevYearYtdAcquired !== null && prevYearYtdEngaged > 0
      ? (prevYearYtdAcquired / prevYearYtdEngaged) * 100
      : null;
  const conversionYoyPct =
    curYtdConversion !== null && prevYtdConversion !== null && prevYtdConversion !== 0
      ? ((curYtdConversion - prevYtdConversion) / prevYtdConversion) * 100
      : null;

  const annualEngagedTotals = series.reduce<Record<number, number>>((acc, p) => {
    const meta = parseQuarterToken(p.quarter);
    if (!meta) return acc;
    acc[meta.year] = (acc[meta.year] ?? 0) + p.engaged;
    return acc;
  }, {});

  const annualAcquiredTotals = series.reduce<Record<number, number>>((acc, p) => {
    const meta = parseQuarterToken(p.quarter);
    if (!meta) return acc;
    acc[meta.year] = (acc[meta.year] ?? 0) + p.acquired;
    return acc;
  }, {});

  const dataYears = Array.from(
    new Set(series.map((p) => parseQuarterToken(p.quarter)?.year).filter((y): y is number => y != null)),
  );
  const minYear = dataYears.length > 0 ? Math.min(...dataYears) : curYear ?? 2024;
  const maxYear = dataYears.length > 0 ? Math.max(...dataYears) : curYear ?? minYear;
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);
  const curYearGoals = curYear !== null ? goalsForYear(props.goals, curYear) : { engaged: undefined, acquired: undefined };

  const annualEngagedData = years.map((year) => {
    const yearGoals = goalsForYear(props.goals, year);
    return {
      year: String(year),
      engaged: annualEngagedTotals[year] ?? 0,
      engagedGoal: yearGoals.engaged,
    };
  });
  const annualAcquiredData = years.map((year) => {
    const yearGoals = goalsForYear(props.goals, year);
    return {
      year: String(year),
      acquired: annualAcquiredTotals[year] ?? 0,
      acquiredGoal: yearGoals.acquired,
    };
  });
  const engagedMax = Math.max(
    curYearGoals.engaged ?? 0,
    ...annualEngagedData.map((d) => d.engaged),
    ...annualEngagedData.map((d) => d.engagedGoal ?? 0),
    1
  );
  const acquiredMax = Math.max(
    curYearGoals.acquired ?? 0,
    ...annualAcquiredData.map((d) => d.acquired),
    ...annualAcquiredData.map((d) => d.acquiredGoal ?? 0),
    1
  );

  const status = (() => {
    if (ytdEngaged === null || ytdAcquired === null) {
      return { label: 'Snapshot' as const, color: 'gray' as const, tooltip: undefined as string | undefined };
    }
    const states: GoalState[] = [];
    if (curYearGoals.engaged != null) {
      states.push(
        getAnnualPaceState({
          ytd: ytdEngaged,
          goalPerYear: curYearGoals.engaged,
          quarterLabel: cur?.quarter ?? '',
          direction: 'min',
        })
      );
    }
    if (curYearGoals.acquired != null) {
      states.push(
        getAnnualPaceState({
          ytd: ytdAcquired,
          goalPerYear: curYearGoals.acquired,
          quarterLabel: cur?.quarter ?? '',
          direction: 'min',
        })
      );
    }
    if (states.length === 0) {
      return { label: 'Snapshot' as const, color: 'gray' as const, tooltip: undefined as string | undefined };
    }
    return getMultiGoalStatus(states);
  })();

  const statusTooltip =
    status.label === 'On track'
      ? undefined
      : buildMultiAnnualGoalStatusTooltip({
          quarterLabel: cur?.quarter ?? '',
          summaryTooltip:
            status.tooltip ??
            (status.label === 'Achieved'
              ? 'Annual goal achieved for all tracked goals.'
              : undefined),
          metrics: [
            ...(curYearGoals.engaged !== undefined && ytdEngaged !== null
              ? [{ label: 'Engaged', ytd: ytdEngaged, goalPerYear: curYearGoals.engaged }]
              : []),
            ...(curYearGoals.acquired !== undefined && ytdAcquired !== null
              ? [{ label: 'Acquired', ytd: ytdAcquired, goalPerYear: curYearGoals.acquired }]
              : []),
          ],
        });

  const tooltipStyles = {
    contentStyle: {
      backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
      border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
      borderRadius: 10,
      color: theme.colorScheme === 'dark' ? '#fff' : '#000',
      padding: '10px 12px',
      maxWidth: 320,
      whiteSpace: 'normal' as const,
      wordBreak: 'break-word' as const,
    },
    labelStyle: { color: theme.colorScheme === 'dark' ? '#fff' : '#000' },
    itemStyle: { color: theme.colorScheme === 'dark' ? '#fff' : '#000' },
  } as const;

  const sharedTooltip = (label: string, value: number, unit: string, goalLabel: string) => (
    <div style={tooltipStyles.contentStyle as any}>
      <div style={{ ...(tooltipStyles.labelStyle as any), fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
      <div style={{ ...(tooltipStyles.itemStyle as any), fontWeight: 900, fontSize: 14, lineHeight: 1.2, marginBottom: 4 }}>
        {value} {value === 1 ? unit : `${unit}s`}
      </div>
      <div style={{ ...(tooltipStyles.itemStyle as any), fontSize: 12, opacity: 0.9 }}>{goalLabel}</div>
    </div>
  );

  return (
    <SafeTCardShell
      title="Client Acquisition"
      subtitle={cur?.quarter ?? 'No data'}
      statusLabel={status.label}
      statusColor={status.color}
      statusTooltip={statusTooltip}
    >
      <Stack spacing="sm">
          <MetricRow
            items={[
              {
                label: 'Engaged (YTD)',
                labelTooltip: 'Calendar year to date potential clients engaged.',
                value: <Text size="lg" weight={800}>{ytdEngaged ?? '—'}</Text>,
                sublabel: (
                  <>
                    {formatYoYSublabel(engagedYoyPct)}
                    {curYearGoals.engaged !== undefined ? (
                      <>
                        <br />
                        {`Goal ${curYearGoals.engaged}/year`}
                      </>
                    ) : null}
                  </>
                ),
              },
              {
                label: 'Acquired (YTD)',
                labelTooltip: 'Calendar year to date clients acquired.',
                value: <Text size="lg" weight={800}>{ytdAcquired ?? '—'}</Text>,
                sublabel: (
                  <>
                    {formatYoYSublabel(acquiredYoyPct)}
                    {curYearGoals.acquired !== undefined ? (
                      <>
                        <br />
                        {`Goal ${curYearGoals.acquired}/year`}
                      </>
                    ) : null}
                  </>
                ),
              },
              {
                label: 'Conversion (YTD)',
                labelTooltip: 'Percentage of calendar year to date engaged clients that were acquired.',
                value: (
                  <Text size="lg" weight={800}>
                    {curYtdConversion !== null ? `${Math.round(curYtdConversion)}%` : '—'}
                  </Text>
                ),
                sublabel: formatYoYSublabel(conversionYoyPct),
              },
            ]}
          />

          <Stack spacing={4} sx={{ marginTop: '0.75rem' }}>
            <Stack spacing={4}>
              <Text size="xs" color="dimmed">Annual Engagements</Text>
              <Box sx={{ height: 140 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={annualEngagedData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.25} vertical={false} />
                    <XAxis dataKey="year" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
                    <YAxis
                      orientation="right"
                      tick={{ fontSize: 11 }}
                      allowDecimals={false}
                      axisLine={false}
                      tickLine={false}
                      width={44}
                      tickMargin={8}
                      domain={[0, engagedMax]}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload || payload.length === 0) return null;
                        const p: any = payload[0]?.payload;
                        if (!p) return null;
                        const goalLabel = p.engagedGoal != null ? `Goal ${p.engagedGoal}/year` : '—';
                        return sharedTooltip(label, p.engaged, 'Engagement', goalLabel);
                      }}
                    />
                    {curYearGoals.engaged != null ? (
                      <ReferenceLine
                        y={curYearGoals.engaged}
                        stroke={safeTColors.goal}
                        strokeWidth={2}
                        strokeDasharray="6 6"
                        ifOverflow="extendDomain"
                      />
                    ) : null}
                    <Bar dataKey="engaged" name="Engaged" fill="#228be6" barSize={18} radius={[6, 6, 0, 0]} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Stack>
            <Stack spacing={4}>
              <Text size="xs" color="dimmed">Annual Acquisitions</Text>
              <Box sx={{ height: 140 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={annualAcquiredData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.25} vertical={false} />
                    <XAxis dataKey="year" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
                    <YAxis
                      orientation="right"
                      tick={{ fontSize: 11 }}
                      allowDecimals={false}
                      axisLine={false}
                      tickLine={false}
                      width={44}
                      tickMargin={8}
                      domain={[0, acquiredMax]}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload || payload.length === 0) return null;
                        const p: any = payload[0]?.payload;
                        if (!p) return null;
                        const goalLabel = p.acquiredGoal !== undefined ? `Goal ${p.acquiredGoal}/year` : '—';
                        return sharedTooltip(label, p.acquired, 'Acquisition', goalLabel);
                      }}
                    />
                    {curYearGoals.acquired != null ? (
                      <ReferenceLine
                        y={curYearGoals.acquired}
                        stroke={safeTColors.goal}
                        strokeWidth={2}
                        strokeDasharray="6 6"
                        ifOverflow="extendDomain"
                      />
                    ) : null}
                    <Bar dataKey="acquired" name="Acquired" fill="#228be6" barSize={18} radius={[6, 6, 0, 0]} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Stack>
          </Stack>
        </Stack>
    </SafeTCardShell>
  );
}
